import { HTMLProps, useEffect, useRef, useState } from "react";

import { CheckCircleIcon, EyeIcon, EyeSlashIcon, MagnifyingGlassIcon, XCircleIcon } from "@heroicons/react/24/outline";

import { usePageNameContext } from "../../../contexts/PageTitleContext";
import useParts from "../../../data/Parts/useParts";
import { IEbayPart } from "../../../models/EbayPart/EbayPart";
import { Table } from "../../../components/base/Table/Table";
import { createColumnHelper } from "@tanstack/react-table";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { ImagePreviewCell } from "../../../components/shared/ImagePreviewCell";
import { useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../../../hooks/useApiHelper";
import CategorySelect from "../../../components/Scrape/CategorySelect";
import { useForm } from "react-hook-form";
import Modal from "../../../layouts/Modal";

function IndeterminateCheckbox({
    indeterminate,
    className = '',
    ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
    const ref = useRef<HTMLInputElement>(null!)

    useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate
        }
    }, [ref, indeterminate])

    return (
        <input
            type="checkbox"
            ref={ref}
            className={className + 'p-3 cursor-pointer w-6 h-6'}
            {...rest}
        />
    )
}

function ConfidenceBadge({ confidence }: { confidence: number | null }) {
    if (confidence === null || confidence === undefined) {
        return <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">N/A</span>;
    }

    const pct = Math.round(confidence * 100);

    let colorClasses: string;
    if (confidence >= 0.85) {
        colorClasses = "bg-green-100 text-green-800";
    } else if (confidence >= 0.6) {
        colorClasses = "bg-yellow-100 text-yellow-800";
    } else {
        colorClasses = "bg-red-100 text-red-800";
    }

    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colorClasses}`}>
            {pct}%
        </span>
    );
}

export default function AiReview() {
    const { saveHandlers } = useDefaultCRUDHandlers("AI review");
    const { register, setValue, getValues, watch } = useForm<any>();
    const { acceptCategory, archive, updatePart, markReviewed, assignCategory } = useParts({ limit: undefined });

    const { get } = useApiHelper();
    const { setInfo } = usePageNameContext();
    const queryClient = useQueryClient();
    const [showAutoAccepted, setShowAutoAccepted] = useState(false);
    const [categorySearch, setCategorySearch] = useState("");
    const [include, setInclude] = useState("");
    const [exclude, setExclude] = useState("");
    const [metadataOpen, setMetadataOpen] = useState(false);
    const [metadataPart, setMetadataPart] = useState<IEbayPart | null>(null);
    const [elasticStatus, setElasticStatus] = useState<boolean | null>(null);
    const [elasticChecking, setElasticChecking] = useState(false);
    const categorySearchWatch = watch("categorySearchCategory");
    const includeWatch = watch("include");
    const excludeWatch = watch("exclude");

    useEffect(() => {
        setInfo({
            name: "AI Categorised Review",
            desc: "Review and approve AI-assigned part categories",
            pageTitle: "AI Review",
        });
    }, []);

    useEffect(() => {
        if (categorySearchWatch !== undefined) {
            setCategorySearch(categorySearchWatch?.name ?? "");
        }
    }, [categorySearchWatch]);

    useEffect(() => {
        if (includeWatch !== undefined) setInclude(includeWatch);
    }, [includeWatch]);

    useEffect(() => {
        if (excludeWatch !== undefined) setExclude(excludeWatch);
    }, [excludeWatch]);

    const colHelper = createColumnHelper<IEbayPart>();

    const handleAccept = (partIds: string[]) => {
        acceptCategory.mutate({ parts: partIds }, {
            onSuccess: () => {
                saveHandlers.onSuccess();
                queryClient.invalidateQueries({ queryKey: ["parts"] });
            },
            onError: () => saveHandlers.onError(),
        });
    };

    const handleCheckElastic = async (itemId: string) => {
        setElasticChecking(true);
        setElasticStatus(null);
        try {
            const res = await get<any>(`/part/elastic-status/${itemId}`);
            setElasticStatus(res.data ?? false);
        } catch {
            setElasticStatus(false);
        }
        setElasticChecking(false);
    };

    const handleReject = (partIds: string[]) => {
        archive.mutate({ parts: partIds }, {
            onSuccess: () => {
                saveHandlers.onSuccess();
                queryClient.invalidateQueries({ queryKey: ["parts"] });
            },
            onError: () => saveHandlers.onError(),
        });
    };

    const handleReassign = (part: IEbayPart) => {
        const newCategory = getValues(`category-${part.id}`);
        if (!newCategory?.id || newCategory.id === part.categoryId) return;

        updatePart.mutate({
            partId: part.id,
            categoryId: newCategory.id,
            categoryName: newCategory.name,
            categoryAccepted: 1,
        }, {
            onSuccess: () => {
                saveHandlers.onSuccess();
                queryClient.invalidateQueries({ queryKey: ["parts"] });
            },
            onError: () => saveHandlers.onError(),
        });
    };

    const handleMarkReviewed = (partIds: string[]) => {
        markReviewed.mutate({ parts: partIds }, {
            onSuccess: () => {
                saveHandlers.onSuccess();
                queryClient.invalidateQueries({ queryKey: ["parts"] });
            },
            onError: () => saveHandlers.onError(),
        });
    };

    const bulkReassignCategory = watch("bulkReassignCategory");

    const handleBulkReassign = (parts: IEbayPart[]) => {
        if (!bulkReassignCategory?.id) {
            saveHandlers.onError();
            return;
        }

        assignCategory.mutate({
            categoryId: bulkReassignCategory.id,
            categoryName: bulkReassignCategory.name,
            parts: parts.map(p => +p.id),
        }, {
            onSuccess: () => {
                saveHandlers.onSuccess();
                queryClient.invalidateQueries({ queryKey: ["parts"] });
            },
            onError: () => saveHandlers.onError(),
        });
    };

    const cols = [
        {
            id: 'select',
            header: ({ table }: any) => (
                <IndeterminateCheckbox
                    {...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                    }}
                />
            ),
            cell: ({ row }: any) => (
                <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
                    <IndeterminateCheckbox
                        {...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        }}
                    />
                </div>
            ),
        },
        colHelper.display({
            id: "image",
            header: "Image",
            cell: (info) => (
                <ImagePreviewCell urls={info.row.original.urls} />
            ),
        }),
        colHelper.accessor("title", {
            header: "Title",
            cell: (info) => (
                <div className="max-w-xs text-wrap" title={info.getValue()}>
                    {info.getValue()}
                </div>
            ),
        }),
        colHelper.display({
            id: "vehicle",
            header: "Vehicle",
            cell: (info) => {
                const { make, model, year } = info.row.original;
                const parts = [make, model, year].filter(Boolean);
                return (
                    <div className="text-sm text-gray-600 whitespace-nowrap">
                        {parts.length > 0 ? parts.join(" ") : "—"}
                    </div>
                );
            },
        }),
        colHelper.accessor("categoryName", {
            header: "AI Category",
            cell: (info) => (
                <span className="font-medium text-orange-700">
                    {info.getValue() || "—"}
                </span>
            ),
        }),
        colHelper.accessor("aiConfidence", {
            header: "Confidence",
            cell: (info) => <ConfidenceBadge confidence={info.getValue()} />,
        }),
        colHelper.accessor("price", {
            header: "Price",
            cell: (info) => (
                <div>£{info.getValue()}</div>
            ),
        }),
        colHelper.accessor("itemSpecifics", {
            header: "Item Specifics",
            cell: (info) => (
                <div className="max-w-48 text-nowrap truncate" title={info.getValue()}>
                    {info.getValue()}
                </div>
            ),
        }),
        colHelper.display({
            header: "Reassign",
            id: "reassign",
            cell: (info) => (
                <span className="flex justify-center relative min-w-48" onClick={(e) => e.stopPropagation()}>
                    <CategorySelect
                        register={register}
                        registerName={`category-${info.row.original.id}`}
                        setValue={setValue}
                        initialValue={info.row.original.categoryId}
                        label={undefined}
                    />
                </span>
            ),
        }),
        colHelper.display({
            header: "Actions",
            cell: (info) => {
                const part = info.row.original;
                return (
                    <div className="flex items-center space-x-3">
                        {showAutoAccepted ? (
                            <button
                                onClick={() => handleMarkReviewed([part.id])}
                                className="inline-flex items-center gap-1 rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20 hover:bg-purple-100 transition-colors"
                                title="Mark as reviewed and hide"
                            >
                                <EyeSlashIcon className="w-4 h-4" />
                                Reviewed
                            </button>
                        ) : (
                            <button
                                onClick={() => handleAccept([part.id])}
                                className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 hover:bg-green-100 transition-colors"
                                title="Accept AI category"
                            >
                                <CheckCircleIcon className="w-4 h-4" />
                                Accept
                            </button>
                        )}
                        <button
                            onClick={() => handleReassign(part)}
                            className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 hover:bg-blue-100 transition-colors"
                            title="Reassign category and accept"
                        >
                            Reassign
                        </button>
                        <button
                            onClick={() => handleReject([part.id])}
                            className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20 hover:bg-red-100 transition-colors"
                            title="Reject / Archive"
                        >
                            <XCircleIcon className="w-4 h-4" />
                            Reject
                        </button>
                        <button
                            onClick={() => {
                                setMetadataPart(part);
                                setElasticStatus(null);
                                setMetadataOpen(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20 hover:bg-gray-100 transition-colors"
                            title="View metadata"
                        >
                            <EyeIcon className="w-4 h-4" />
                            Details
                        </button>
                    </div>
                );
            },
        }),
    ];

    return (
        <div className="flex flex-col space-y-4">
            <div className="grid grid-cols-3 gap-4">
                <div className="rounded-lg bg-white p-4 shadow">
                    <p className="text-sm text-gray-500">Threshold</p>
                    <p className="text-2xl font-semibold text-gray-900">85%</p>
                    <p className="text-xs text-gray-400 mt-1">Auto-accept confidence</p>
                </div>
                <div className="rounded-lg bg-white p-4 shadow">
                    <p className="text-sm text-gray-500">Below shows</p>
                    <p className="text-2xl font-semibold text-orange-600">{showAutoAccepted ? "Auto Accepted" : "Needs Review"}</p>
                    <p className="text-xs text-gray-400 mt-1">
                        {showAutoAccepted ? "AI categorised and already accepted" : "AI categorised, not yet accepted"}
                    </p>
                </div>
                <div className="rounded-lg bg-white p-4 shadow">
                    <p className="text-sm text-gray-500">Actions</p>
                    <p className="text-sm text-gray-700 mt-1">
                        <strong>Accept</strong> to confirm the AI category, or <strong>Reject</strong> to archive.
                        Use the Reassign dropdown to pick a different category before accepting.
                    </p>
                    <button
                        onClick={() => setShowAutoAccepted((prev) => !prev)}
                        className="mt-3 inline-flex items-center rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700 transition-colors"
                    >
                        {showAutoAccepted ? "Show Needs Review" : "Show Auto Accepted"}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
                <div>
                    <div className="text-sm text-gray-700">Category Search</div>
                    <div className="!mt-1">
                        <CategorySelect
                            register={register}
                            registerName="categorySearchCategory"
                            setValue={setValue}
                            label={undefined}
                        />
                    </div>
                </div>
                <div>
                    <div className="text-sm text-gray-700">Includes</div>
                    <div className="relative flex items-stretch focus-within:z-10 w-full !mt-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </div>
                        <input
                            type="text"
                            className="w-full pl-10 py-2 border sm:text-sm"
                            placeholder="Include by title"
                            {...register("include")}
                        />
                    </div>
                </div>
                <div>
                    <div className="text-sm text-gray-700">Excludes</div>
                    <div className="relative flex items-stretch focus-within:z-10 w-full !mt-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </div>
                        <input
                            type="text"
                            className="w-full pl-10 py-2 border sm:text-sm"
                            placeholder="Exclude by title"
                            {...register("exclude")}
                        />
                    </div>
                </div>
                <div>
                    <div className="text-sm text-gray-700">Bulk Reassign To</div>
                    <div className="!mt-1">
                        <CategorySelect
                            register={register}
                            registerName="bulkReassignCategory"
                            setValue={setValue}
                            label={undefined}
                        />
                    </div>
                </div>
            </div>

            <Table
                url="/part"
                queryKey={["parts", "ai-review", showAutoAccepted ? "auto-accepted" : "needs-review"]}
                columns={cols}
                externalFilters={{
                    hideCategorised: false,
                    hideAccepted: !showAutoAccepted,
                    aiCategorisedOnly: true,
                    categorySearch,
                    include,
                    exclude,
                    hideReviewed: showAutoAccepted,
                }}
                bulkActions={[
                    ...(showAutoAccepted ? [
                        {
                            label: "Mark Reviewed",
                            onClick: (parts: IEbayPart[]) => handleMarkReviewed(parts.map(p => p.id)),
                        },
                    ] : [
                        {
                            label: "Accept Selected",
                            onClick: (parts: IEbayPart[]) => handleAccept(parts.map(p => p.id)),
                        },
                    ]),
                    {
                        label: "Reassign Selected",
                        onClick: (parts: IEbayPart[]) => handleBulkReassign(parts),
                    },
                    {
                        label: "Reject Selected",
                        onClick: (parts: IEbayPart[]) => handleReject(parts.map(p => p.id)),
                    },
                ]}
            />

            <Modal open={metadataOpen} setOpen={setMetadataOpen} width="sm:max-w-4xl">
                <div className="space-y-4">
                    <h1 className="text-xl font-semibold">Part metadata</h1>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-700">Item ID</h2>
                        <p className="text-sm text-gray-900">{metadataPart?.itemId || "—"}</p>
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-700">Title</h2>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.title || "—"}</p>
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-700">Item Specifics</h2>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.itemSpecifics || "—"}</p>
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-700">Quick Specs</h2>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.quickspecs || "—"}</p>
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-700">Vehicle Specifications</h2>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.vehicleSpecifications || "—"}</p>
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-700">Part Info</h2>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.partInfo || "—"}</p>
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-700">Part Comment</h2>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{metadataPart?.partComment || "—"}</p>
                    </div>
                    <div className="border-t pt-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => metadataPart && handleCheckElastic(metadataPart.itemId)}
                                disabled={elasticChecking}
                                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                            >
                                {elasticChecking ? "Checking..." : "Check Import"}
                            </button>
                            {elasticStatus !== null && (
                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${elasticStatus ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                    {elasticStatus ? "Synced to Elasticsearch" : "Not in Elasticsearch"}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
